'use client';

import { Play, X } from 'lucide-react';
import { useRef, useState, useEffect, useCallback, useMemo, useId } from 'react';
import RespImage from '@/app/_components/core/resp-image';
import { VideoControls } from '@/app/_components/shared/video/components/video-controls';
import type { VideoPlayerHandle, VideoPlayerState } from '@/app/_components/shared/video/video.types';
import { useConsentManager } from '@/app/_hooks/use-consent-manager';
import { useGlobalVideoPause } from '@/app/_hooks/use-global-video-pause';
import { useMediaQuery } from '@/app/_hooks/use-media-query';
import { useYouTubePlayer } from '@/app/_hooks/use-youtube-player';
import { cn, emptyToUndefined } from '@/app/_lib/utils';
import type { SmartVideoProps } from './smart-video.types';

// True when the CMS supplied a usable poster image (srcset candidates present).
function hasPosterImage(previewImage: SmartVideoProps['previewImage']): boolean {
  return Boolean(
    previewImage &&
      Array.isArray(previewImage.srcsetCandidates) &&
      previewImage.srcsetCandidates.length > 0,
  );
}

// Poster shown before the player mounts: CMS image if present, else the YouTube
// thumbnail. Module-scope (not nested in YouTubePlayer) to keep the component's
// cognitive complexity down.
function YouTubePoster({
  className,
  hasPoster,
  previewImage,
  ytThumbnail,
  title,
  priority,
  imageSizes,
}: {
  className: string;
  hasPoster: boolean | undefined;
  previewImage: SmartVideoProps['previewImage'];
  ytThumbnail: string | undefined;
  title: SmartVideoProps['title'];
  priority: SmartVideoProps['priority'];
  imageSizes: SmartVideoProps['imageSizes'];
}) {
  if (hasPoster && previewImage) {
    return (
      <RespImage
        srcsetCandidates={previewImage.srcsetCandidates as { imageUrl: string; width: number; height: number }[]}
        alt={emptyToUndefined(previewImage.altText) ?? emptyToUndefined(title) ?? ''}
        sizes={imageSizes}
        priority={priority}
        className={className}
      />
    );
  }
  if (ytThumbnail) {
    // eslint-disable-next-line @next/next/no-img-element -- external YT thumbnail, no srcset/dims (RespImage handles the poster path above)
    return <img src={ytThumbnail} alt={title ?? ''} className={className} fetchPriority={priority ? 'high' : undefined} loading={priority ? 'eager' : undefined} />;
  }
  return null;
}

export function YouTubePlayer({
  youtubeId,
  previewImage,
  title,
  imageSizes,
  priority = false,
  consentLabels,
  isActive = true,
  autoPlay = false,
  onPlay,
  trackingPrefix,
}: SmartVideoProps) {
  const hasPoster = hasPosterImage(previewImage);
  const ytThumbnail = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : undefined;

  const { hasConsent, isReady: consentReady, acceptCategory } = useConsentManager({ category: 'marketing' });
  // Local consent: stays true for this session even if Cookiebot revokes (prevents crash)
  const [consented, setConsented] = useState(false);
  const canPlay = hasConsent || consented;

  const containerId = useId().replace(/:/g, '_');
  const containerRef = useRef<HTMLDivElement>(null);
  const ytWrapperRef = useRef<HTMLDivElement>(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showPoster, setShowPoster] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const [isFakeFullscreen, setIsFakeFullscreen] = useState(false);

  // IntersectionObserver: preload player when video is near viewport on touch devices
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isTouchDevice || !canPlay) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsInView(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => { observer.disconnect(); };
  }, [isTouchDevice, canPlay]);

  // Preload on touch devices when visible + consent given
  const preloading = isTouchDevice && canPlay && isInView && !showPlayer;
  const playerEnabled = (showPlayer || preloading) && canPlay;

  const [playerState, setPlayerState] = useState<VideoPlayerState>({
    isPlaying: false, currentTime: 0, duration: 0,
    volume: 1, isMuted: false, isEnded: false, isReady: false, playbackRate: 1,
  });

  const handleReady = useCallback(() => { setPlayerState((prev) => ({ ...prev, isReady: true })); }, []);
  const handleStateChange = useCallback((state: number) => {
    setPlayerState((prev) => ({ ...prev, isPlaying: state === 1 }));
    if (state === 0) setShowPoster(true);
  }, []);
  const { player, isReady: ytReady } = useYouTubePlayer({
    containerId: `yt-${containerId}`,
    videoId: youtubeId ?? '',
    enabled: playerEnabled,
    autoplay: !isTouchDevice && showPlayer,
    onReady: handleReady,
    onStateChange: handleStateChange,
  });

  const { notifyPlay } = useGlobalVideoPause(
    // eslint-disable-next-line react-hooks/exhaustive-deps -- player is a stable ref from useYouTubePlayer; React Compiler requires it omitted
    useCallback(() => { try { player.current?.pauseVideo(); } catch { /* player not ready */ } }, [])
  );

  // Poll YT for time/duration (YT API has no timeupdate event, only way)
  useEffect(() => {
    if (!ytReady || !playerState.isPlaying) return;
    const id = setInterval(() => {
      const p = player.current;
      if (!p) return;
      setPlayerState((prev) => ({
        ...prev,
        currentTime: p.getCurrentTime(),
        duration: p.getDuration(),
        volume: p.getVolume() / 100,
        isMuted: p.isMuted(),
      }));
    }, 250);
    return () => { clearInterval(id); };
  }, [ytReady, playerState.isPlaying, player]);

  // Slide change: pause when leaving, resume when returning
  useEffect(() => {
    if (!player.current || !ytReady) return;
    if (!isActive) {
      player.current.pauseVideo();
    } else if (autoPlay && !showPoster) {
      // Resume: user already played this video, Safari allows playVideo() after prior interaction
      notifyPlay();
      player.current.playVideo();
    }
  }, [isActive, autoPlay, showPoster, player, ytReady, notifyPlay]);

  // Auto-play on slide enter — desktop only (touch: user taps play button instead).
  // Legitimate effect: starts playback when the carousel slide becomes active
  // (external isActive prop). The setState calls sync React's render to the
  // video widget — not a derived value, so the rule is intentionally disabled.
  useEffect(() => {
    if (isTouchDevice) return;
    if (isActive && autoPlay && canPlay && showPoster) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setShowPoster(false);
      setShowPlayer(true);
      /* eslint-enable react-hooks/set-state-in-effect */
      notifyPlay();
      onPlay?.();
    }
  }, [isActive, autoPlay, canPlay, showPoster, notifyPlay, onPlay, isTouchDevice]);

  // Manage YouTube div outside React's control (prevents removeChild crash)
  useEffect(() => {
    if (!playerEnabled || !ytWrapperRef.current) return;
    const wrapper = ytWrapperRef.current;
    if (!document.getElementById(`yt-${containerId}`)) {
      const div = document.createElement('div');
      div.id = `yt-${containerId}`;
      div.className = 'size-full';
      wrapper.appendChild(div);
    }
    return () => { while (wrapper.firstChild) wrapper.removeChild(wrapper.firstChild); };
  }, [playerEnabled, containerId]);

  const playerHandle = useMemo<VideoPlayerHandle>(() => ({
    play: () => { player.current?.playVideo(); notifyPlay(); onPlay?.(); },
    pause: () => player.current?.pauseVideo(),
    seek: (time: number) => player.current?.seekTo(time, true),
    setVolume: (vol: number) => player.current?.setVolume(vol * 100),
    setMuted: (muted: boolean) => { if (muted) player.current?.mute(); else player.current?.unMute(); },
    setPlaybackRate: (rate: number) => {
      player.current?.setPlaybackRate(rate);
      setPlayerState((prev) => ({ ...prev, playbackRate: rate }));
    },
    requestFullscreen: () => {
      const el = containerRef.current;
      if (!el) return;
      if (document.fullscreenEnabled) void el.requestFullscreen();
      else if ((document as Document & { webkitFullscreenEnabled?: boolean }).webkitFullscreenEnabled) {
        (el as HTMLElement & { webkitRequestFullscreen: () => void }).webkitRequestFullscreen();
      } else {
        setIsFakeFullscreen(true);
      }
    },
    supportsFullscreen: true,
    requestPip: () => {
      /* YouTube embeds have no Picture-in-Picture support */
    },
  }), [notifyPlay, onPlay]); // eslint-disable-line react-hooks/exhaustive-deps -- player is a stable ref from useYouTubePlayer; React Compiler requires it omitted

  const startPlayback = useCallback(() => {
    setShowPoster(false);
    notifyPlay();
    setShowPlayer(true);
    if (player.current && ytReady) {
      player.current.seekTo(0, true);
      player.current.playVideo();
    }
    onPlay?.();
  }, [ytReady, notifyPlay, onPlay]); // eslint-disable-line react-hooks/exhaustive-deps -- player is a stable ref from useYouTubePlayer; React Compiler requires it omitted

  // Fake fullscreen via <dialog> - showModal() goes to browser top layer, escaping all transforms
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isFakeFullscreen) {
      if (dialog.open) dialog.close();
      dialog.showModal();
    } else {
      if (dialog.open) dialog.close();
      dialog.show();
    }
  }, [isFakeFullscreen]);

  // Close on Escape key
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (e: Event) => { e.preventDefault(); setIsFakeFullscreen(false); };
    dialog.addEventListener('cancel', handleCancel);
    return () => { dialog.removeEventListener('cancel', handleCancel); };
  }, []);

  // Loading
  if (!consentReady) {
    return (
      <div className="flex aspect-video w-full items-center justify-center bg-muted" role="status" aria-label="Video wird geladen">
        <div className="size-8 animate-spin rounded-full border-4 border-muted-foreground/20 border-t-muted-foreground" />
        <span className="sr-only">Video wird geladen...</span>
      </div>
    );
  }

  // Consent screen
  if (!canPlay) {
    const handleConsent = () => {
      acceptCategory();
      setConsented(true);
      if (!isTouchDevice) startPlayback();
    };
    return (
      <div
        role="button"
        tabIndex={0}
        className="relative flex aspect-video w-full cursor-pointer items-center justify-center overflow-hidden bg-black"
        onClick={handleConsent}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleConsent();
          }
        }}
      >
        <YouTubePoster
          className="absolute inset-0 size-full object-cover opacity-40"
          hasPoster={hasPoster}
          previewImage={previewImage}
          ytThumbnail={ytThumbnail}
          title={title}
          priority={priority}
          imageSizes={imageSizes}
        />
        <div className="absolute inset-0 bg-overlay-poster" />
        <div className="relative z-10 flex flex-col items-center gap-2 px-4 text-center text-white md:gap-4">
          <Play className="size-6 md:size-12" />
          {consentLabels?.consentTitle && <p className="text-sm font-medium md:text-lg">{consentLabels.consentTitle}</p>}
          {consentLabels?.consentDescription && <p className="max-w-md text-xs text-white/70 md:text-sm">{consentLabels.consentDescription}</p>}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); acceptCategory(); setConsented(true); if (!isTouchDevice) startPlayback(); }}
            className="mt-1 cursor-pointer rounded-lg bg-overlay-glass px-4 py-1.5 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-overlay-glass-strong md:mt-0 md:px-6 md:py-2.5 md:text-sm"
            data-track-id={trackingPrefix ? `${trackingPrefix}-consent-play` : undefined}
          >
            {emptyToUndefined(consentLabels?.consentButton) ?? 'Video laden'}
          </button>
        </div>
      </div>
    );
  }

  // Player - content always lives inside <dialog> to avoid iframe destruction on DOM move
  return (
    <dialog
      ref={dialogRef}
      className={cn(
        'border-none bg-black p-0',
        isFakeFullscreen
          ? 'm-0 h-dvh w-dvw max-h-dvh max-w-none backdrop:bg-black'
          : 'relative block aspect-video w-full'
      )}
    >
      <div className={cn(
        'relative overflow-hidden bg-black',
        isFakeFullscreen ? 'flex size-full items-center justify-center' : 'size-full'
      )}>
        <div ref={containerRef} className={cn(
          'relative overflow-hidden',
          isFakeFullscreen ? 'aspect-video h-full max-w-full' : 'size-full'
        )}>
          {playerEnabled && (
            <>
              <div ref={ytWrapperRef} className={cn('pointer-events-none absolute inset-0 size-full', showPoster && 'invisible')} />
              {!showPoster && ytReady && (
                <button
                  type="button"
                  className="absolute inset-0 z-10 w-full cursor-pointer border-none bg-transparent"
                  aria-label={playerState.isPlaying ? 'Video pausieren' : 'Video abspielen'}
                  onClick={() => {
                    const p = player.current;
                    if (!p) return;
                    if (p.getPlayerState() === 1) p.pauseVideo();
                    else { notifyPlay(); p.playVideo(); }
                  }}
                />
              )}
            </>
          )}

          <button
            type="button"
            className={cn(
              'absolute inset-0 z-10 flex w-full cursor-pointer items-center justify-center border-none bg-transparent transition-opacity duration-500',
              showPoster ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
            onClick={startPlayback}
            aria-label="Video abspielen"
            data-track-id={trackingPrefix ? `${trackingPrefix}-play` : undefined}
          >
            <YouTubePoster
              className="absolute inset-0 size-full object-cover"
              hasPoster={hasPoster}
              previewImage={previewImage}
              ytThumbnail={ytThumbnail}
              title={title}
              priority={priority}
              imageSizes={imageSizes}
            />
            <span className="relative z-10 flex size-16 items-center justify-center rounded-full bg-overlay-glass-strong text-white backdrop-blur-md transition-transform hover:scale-110">
              <Play className="size-5 fill-current" />
            </span>
          </button>

          {!showPoster && ytReady && (
            <div className="absolute inset-x-0 bottom-0 z-20">
              <VideoControls playerHandle={playerHandle} playerState={playerState} videoType="youtube" />
            </div>
          )}
        </div>

        {/* Fake fullscreen close button */}
        {isFakeFullscreen && (
          <button
            type="button"
            onClick={() => { setIsFakeFullscreen(false); }}
            className="absolute right-4 top-4 z-30 flex size-10 cursor-pointer items-center justify-center rounded-full bg-overlay-control text-white backdrop-blur-sm transition-colors hover:bg-overlay-control-hover"
            aria-label="Fullscreen beenden"
          >
            <X className="size-5" />
          </button>
        )}
      </div>
    </dialog>
  );
}
